import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, ArrowClockwise, MagnifyingGlass, X, CheckCircle, XCircle, PaperPlaneRight,
  PencilSimple, Trash, Clock, FileText, Warning, Lightning, ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_META = {
  rascunho: { label: "Rascunho", bg: "bg-[#334155]/40", text: "text-[#94A3B8]", border: "border-[#334155]" },
  em_analise: { label: "Em Análise", bg: "bg-[#FFDF00]/15", text: "text-[#FFDF00]", border: "border-[#FFDF00]/40" },
  aprovada: { label: "Aprovada", bg: "bg-[#1E4FBF]/15", text: "text-[#3B82F6]", border: "border-[#1E4FBF]/40" },
  executada: { label: "Executada", bg: "bg-[#009C3B]/15", text: "text-[#12B850]", border: "border-[#009C3B]/40" },
  rejeitada: { label: "Rejeitada", bg: "bg-[#EF4444]/15", text: "text-[#F87171]", border: "border-[#EF4444]/40" },
};

function fmtMoeda(v) {
  return `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function RenovacoesSaldo() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [status, setStatus] = useState("");
  const [municipioId, setMunicipioId] = useState("");
  const [municipios, setMunicipios] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendentes, setPendentes] = useState([]);
  const [loadingPend, setLoadingPend] = useState(false);
  const [limiarPct, setLimiarPct] = useState(0);
  const [criandoRascunho, setCriandoRascunho] = useState(null);

  const loadPendentes = () => {
    setLoadingPend(true);
    const p = new URLSearchParams();
    p.set("limiar_pct", String(limiarPct));
    if (isAdmin && municipioId) p.set("municipio_id", municipioId);
    api.get(`/renovacoes-saldo/pendentes?${p.toString()}`)
      .then((r) => setPendentes(r.data?.items || []))
      .catch(() => setPendentes([]))
      .finally(() => setLoadingPend(false));
  };

  const criarRascunhoPendente = async (contractId, numero) => {
    setCriandoRascunho(contractId);
    try {
      const r = await api.post(`/renovacoes-saldo/pendentes/criar-rascunho/${contractId}`);
      toast.success(`${r.data.numero_gerado} criada em rascunho para ${numero}`);
      loadPendentes();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setCriandoRascunho(null); }
  };

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (isAdmin && municipioId) p.set("municipio_id", municipioId);
    api.get(`/renovacoes-saldo?${p.toString()}`)
      .then((r) => setItems(r.data?.items || []))
      .finally(() => setLoading(false));
    api.get(`/renovacoes-saldo/stats${isAdmin && municipioId ? `?municipio_id=${municipioId}` : ""}`)
      .then((r) => setStats(r.data || {}));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, municipioId]);
  useEffect(() => { loadPendentes(); /* eslint-disable-next-line */ }, [limiarPct, municipioId]);
  useEffect(() => {
    if (isAdmin) api.get("/municipalities").then((r) => setMunicipios(r.data || []));
  }, [isAdmin]);

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="uppercase-label flex items-center gap-1.5">
            <ArrowClockwise size={12} weight="duotone" className="text-[#009C3B]" /> Renovação de ciclos
          </div>
          <h1 className="font-heading text-4xl mt-1">Renovação de Saldos</h1>
          <p className="text-sm text-[#94A3B8] mt-1">
            Fecha o ciclo atual (preservando as NFs lançadas) e abre um novo saldo renovado para o contrato.
          </p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="ren-new"
                className="px-4 py-2.5 rounded-md bg-gradient-to-r from-[#009C3B] via-[#FFDF00] to-[#002776] text-white font-semibold flex items-center gap-2 shadow-sm">
          <Plus size={14} weight="bold" /> Nova Renovação
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Card label="Rascunho" qtd={stats.rascunho?.qtd} valor={stats.rascunho?.valor} tone="rascunho" />
        <Card label="Em Análise" qtd={stats.em_analise?.qtd} valor={stats.em_analise?.valor} tone="em_analise" />
        <Card label="Aprovadas" qtd={stats.aprovada?.qtd} valor={stats.aprovada?.valor} tone="aprovada" />
        <Card label="Executadas" qtd={stats.executada?.qtd} valor={stats.executada?.valor} tone="executada" />
        <Card label="Rejeitadas" qtd={stats.rejeitada?.qtd} valor={stats.rejeitada?.valor} tone="rejeitada" />
      </div>

      {/* Pendentes de renovação — contratos com saldo zerado/baixo sem renovação em aberto */}
      <div className="tce-card p-5 mb-5 border border-[#EF4444]/30" data-testid="pendentes-renovacao">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md border border-[#EF4444]/40 bg-[#EF4444]/10 flex items-center justify-center">
              <Warning size={20} weight="duotone" className="text-[#F87171]" />
            </div>
            <div>
              <div className="uppercase-label flex items-center gap-1.5">
                <Lightning size={10} weight="duotone" className="text-[#F59E0B]" /> Detecção Automática
              </div>
              <h2 className="font-heading text-xl mt-0.5">
                Contratos com saldo esgotado — pendentes de renovação
                <span className="ml-2 text-sm text-[#F87171] font-mono">({pendentes.length})</span>
              </h2>
              <p className="text-xs text-[#94A3B8] mt-0.5">
                Listagem automática de contratos cujo valor executado atingiu o limite e que ainda não possuem renovação em curso.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-[#94A3B8]">Limiar:</label>
            <select value={limiarPct} onChange={(e) => setLimiarPct(Number(e.target.value))}
                    className="tce-input px-2 py-1.5 rounded-md text-xs" data-testid="pend-limiar">
              <option value={0}>Saldo ≤ 0 (zerado)</option>
              <option value={5}>Saldo ≤ 5%</option>
              <option value={10}>Saldo ≤ 10%</option>
              <option value={20}>Saldo ≤ 20%</option>
            </select>
            <button onClick={loadPendentes} className="p-1.5 rounded border border-[#334155] hover:border-[#FFDF00]"
                    title="Recarregar" data-testid="pend-reload">
              <ArrowClockwise size={14} />
            </button>
          </div>
        </div>

        {loadingPend && <div className="text-center text-xs text-[#94A3B8] py-6">Verificando contratos…</div>}
        {!loadingPend && pendentes.length === 0 && (
          <div className="text-center text-xs text-[#94A3B8] py-6 border border-dashed border-[#334155] rounded">
            ✓ Nenhum contrato pendente de renovação neste filtro.
          </div>
        )}
        {!loadingPend && pendentes.length > 0 && (
          <div className="overflow-x-auto -mx-5">
            <table className="w-full text-xs">
              <thead className="bg-[#0F172A] border-y border-[#334155]">
                <tr className="text-left text-[#94A3B8]">
                  <th className="px-5 py-2 font-medium">Contrato</th>
                  <th className="px-3 py-2 font-medium">Objeto</th>
                  <th className="px-3 py-2 font-medium">Fornecedor</th>
                  <th className="px-3 py-2 font-medium text-right">Valor</th>
                  <th className="px-3 py-2 font-medium text-right">Executado</th>
                  <th className="px-3 py-2 font-medium text-right">Saldo</th>
                  <th className="px-3 py-2 font-medium text-center">Severidade</th>
                  <th className="px-5 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {pendentes.slice(0, 20).map((p) => {
                  const sevColor = p.severidade === "critico" ? "#EF4444" :
                                     p.severidade === "alto" ? "#F59E0B" : "#FBBF24";
                  const sevBg = p.severidade === "critico" ? "bg-[#EF4444]/15 text-[#F87171]" :
                                   p.severidade === "alto" ? "bg-[#F59E0B]/15 text-[#F59E0B]" :
                                                                "bg-[#FBBF24]/15 text-[#FBBF24]";
                  return (
                    <tr key={p.contract_id} className="border-b border-[#334155]/60 hover:bg-[#334155]/15"
                        data-testid={`pend-row-${p.contract_id}`}>
                      <td className="px-5 py-2.5 font-mono text-[#F59E0B]">{p.numero_contrato}</td>
                      <td className="px-3 py-2.5 max-w-[280px] truncate" title={p.objeto}>{p.objeto}</td>
                      <td className="px-3 py-2.5 max-w-[200px] truncate" title={p.fornecedor_nome}>{p.fornecedor_nome || "—"}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{fmtMoeda(p.valor)}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{fmtMoeda(p.valor_executado)}</td>
                      <td className="px-3 py-2.5 text-right font-mono font-semibold" style={{color: sevColor}}>
                        {fmtMoeda(p.saldo)}
                        <span className="text-[9px] block opacity-70">{p.pct_saldo}%</span>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${sevBg}`}>
                          {p.severidade}
                        </span>
                      </td>
                      <td className="px-5 py-2.5 text-right">
                        <button onClick={() => criarRascunhoPendente(p.contract_id, p.numero_contrato)}
                                disabled={criandoRascunho === p.contract_id}
                                className="px-3 py-1 rounded bg-gradient-to-r from-[#009C3B] to-[#002776] text-white text-[10px] font-semibold flex items-center gap-1 disabled:opacity-50"
                                data-testid={`pend-iniciar-${p.contract_id}`}>
                          {criandoRascunho === p.contract_id ? "Criando…" : <>Iniciar renovação <ArrowRight size={10} weight="bold" /></>}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {pendentes.length > 20 && (
              <div className="px-5 py-2 text-center text-[10px] text-[#64748B]">
                Mostrando 20 de {pendentes.length} contratos — ajuste o filtro para refinar
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="tce-input px-3 py-2 rounded-md text-sm" data-testid="ren-status">
          <option value="">Todos os status</option>
          <option value="rascunho">Rascunho</option>
          <option value="em_analise">Em Análise</option>
          <option value="aprovada">Aprovadas</option>
          <option value="executada">Executadas</option>
          <option value="rejeitada">Rejeitadas</option>
        </select>
        {isAdmin && (
          <select value={municipioId} onChange={(e) => setMunicipioId(e.target.value)}
                  className="tce-input px-3 py-2 rounded-md text-sm" data-testid="ren-muni">
            <option value="">Todos os municípios</option>
            {municipios.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
          </select>
        )}
      </div>

      <div className="tce-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F172A] border-b border-[#334155]">
            <tr className="text-left text-[#94A3B8]">
              <th className="px-4 py-3 font-medium">Nº</th>
              <th className="px-4 py-3 font-medium">Contrato</th>
              <th className="px-4 py-3 font-medium">Fornecedor</th>
              <th className="px-4 py-3 font-medium text-center">Ciclo</th>
              <th className="px-4 py-3 font-medium">Novo exercício</th>
              <th className="px-4 py-3 font-medium text-right">Valor renovado</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" className="px-4 py-10 text-center text-[#94A3B8]">Carregando…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-10 text-center text-[#94A3B8]">Nenhuma renovação cadastrada</td></tr>
            )}
            {items.map((r) => {
              const sm = STATUS_META[r.status] || STATUS_META.rascunho;
              return (
                <tr key={r.id} className="border-b border-[#334155]/60 hover:bg-[#334155]/20"
                    data-testid={`ren-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span>{r.numero_gerado}</span>
                      {r.aditivo_origem_numero && (
                        <span title={`Gerada automaticamente a partir do Aditivo ${r.aditivo_origem_numero}`}
                              className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-[#F59E0B]/20 text-[#F59E0B] font-sans"
                              data-testid={`ren-origem-${r.id}`}>
                          ← ADT {r.aditivo_origem_numero}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.contract_numero}</div>
                    <div className="text-xs text-[#94A3B8] truncate max-w-[280px]">{r.contract_objeto}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{r.fornecedor_nome || "—"}</td>
                  <td className="px-4 py-3 text-center text-xs">
                    <span className="px-2 py-0.5 rounded bg-[#334155]/50">{r.ciclo_fechado}→{r.ciclo_novo}</span>
                  </td>
                  <td className="px-4 py-3 text-xs">{r.novo_exercicio}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtMoeda(r.valor_renovado)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${sm.bg} ${sm.text} ${sm.border}`}>
                      {sm.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => { setEditing(r); setShowForm(true); }}
                            className="px-2 py-1 rounded border border-[#334155] hover:border-[#FFDF00] text-xs"
                            data-testid={`ren-open-${r.id}`}>Abrir</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && (
        <RenovacaoFormModal
          ren={editing}
          user={user}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function Card({ label, qtd, valor, tone }) {
  const palette = {
    rascunho: "border-[#334155] text-[#94A3B8]",
    em_analise: "border-[#FFDF00]/40 text-[#FFDF00]",
    aprovada: "border-[#1E4FBF]/40 text-[#3B82F6]",
    executada: "border-[#009C3B]/40 text-[#12B850]",
    rejeitada: "border-[#EF4444]/40 text-[#F87171]",
  };
  return (
    <div className={`tce-card p-4 border ${palette[tone]}`}>
      <div className="uppercase-label text-[10px]">{label}</div>
      <div className="font-heading text-2xl mt-1">{qtd || 0}</div>
      <div className="text-xs text-[#94A3B8] mt-1 font-mono">{fmtMoeda(valor)}</div>
    </div>
  );
}


function RenovacaoFormModal({ ren, user, onClose, onSaved }) {
  const isEdit = !!ren;
  const canApprove = user?.role === "admin" || user?.role === "gestor";
  const [current, setCurrent] = useState(ren);
  const [contratos, setContratos] = useState([]);
  const [contratoSel, setContratoSel] = useState(null);
  const [form, setForm] = useState({
    contract_id: ren?.contract_id || "",
    novo_exercicio: ren?.novo_exercicio || new Date().getFullYear() + 1,
    valor_renovado: ren?.valor_renovado || 0,
    justificativa: ren?.justificativa || "",
  });
  const [parecer, setParecer] = useState("");
  const [busy, setBusy] = useState(false);
  const sm = current ? STATUS_META[current.status] : null;
  const editable = !current || current.status === "rascunho" || current.status === "em_analise";

  useEffect(() => {
    if (!isEdit) {
      api.get("/contracts?status=ativo&limit=1000").then((r) => setContratos(r.data?.items || []));
    }
  }, [isEdit]);

  useEffect(() => {
    if (form.contract_id && contratos.length) {
      const c = contratos.find((x) => x.id === form.contract_id);
      setContratoSel(c || null);
      if (c && !form.valor_renovado) {
        setForm((f) => ({ ...f, valor_renovado: c.valor }));
      }
    }
  }, [form.contract_id, contratos]);

  const salvar = async () => {
    if (!isEdit) {
      if (!form.contract_id || !form.valor_renovado || !form.justificativa) {
        toast.error("Preencha contrato, valor renovado e justificativa"); return;
      }
    }
    setBusy(true);
    try {
      if (!isEdit) {
        const r = await api.post("/renovacoes-saldo", {
          contract_id: form.contract_id,
          novo_exercicio: Number(form.novo_exercicio),
          valor_renovado: Number(form.valor_renovado),
          justificativa: form.justificativa,
        });
        toast.success(`Renovação ${r.data.numero_gerado} criada`);
        setCurrent(r.data);
      } else {
        const r = await api.patch(`/renovacoes-saldo/${current.id}`, {
          novo_exercicio: Number(form.novo_exercicio),
          valor_renovado: Number(form.valor_renovado),
          justificativa: form.justificativa,
        });
        toast.success("Renovação atualizada");
        setCurrent(r.data);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setBusy(false); }
  };

  const transicao = async (acao, path, confirmMsg) => {
    if (!current) { toast.error("Salve primeiro"); return; }
    if (acao === "rejeitar" && !parecer) { toast.error("Informe o parecer"); return; }
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const r = await api.post(`/renovacoes-saldo/${current.id}/${path}`, { parecer: parecer || null });
      toast.success(`Renovação ${r.data.status}`);
      setCurrent(r.data);
      setParecer("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const excluir = async () => {
    if (!current) { onClose(); return; }
    if (!window.confirm("Excluir esta renovação?")) return;
    try {
      await api.delete(`/renovacoes-saldo/${current.id}`);
      toast.success("Excluída");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const infoContrato = isEdit ? {
    numero: current.contract_numero, objeto: current.contract_objeto, fornecedor: current.fornecedor_nome,
    valor_original: current.valor_original, executado: current.valor_executado_no_fechamento,
    saldo: current.valor_original - current.valor_executado_no_fechamento,
  } : contratoSel ? {
    numero: contratoSel.numero_contrato || contratoSel.numero,
    objeto: contratoSel.objeto, fornecedor: contratoSel.fornecedor_nome,
    valor_original: contratoSel.valor, executado: contratoSel.valor_executado || 0,
    saldo: contratoSel.valor - (contratoSel.valor_executado || 0),
  } : null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-4xl max-h-[92vh] bg-[#1E293B] border border-[#334155] rounded-lg flex flex-col overflow-hidden">
        <div className="p-4 border-b border-[#334155] flex items-start justify-between">
          <div>
            <div className="uppercase-label flex items-center gap-1.5">
              <ArrowClockwise size={12} weight="duotone" className="text-[#009C3B]" />
              {isEdit ? `Renovação ${current?.numero_gerado}` : "Nova renovação de saldo"}
            </div>
            <h2 className="font-heading text-2xl mt-1">
              {isEdit ? `Ciclo ${current?.ciclo_fechado} → ${current?.ciclo_novo}` : "Fechar ciclo e renovar saldo"}
            </h2>
            {sm && (
              <span className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${sm.bg} ${sm.text} ${sm.border}`}>
                {sm.label}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#334155] rounded" data-testid="ren-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!isEdit && (
            <div>
              <label className="uppercase-label block mb-1">Contrato ativo *</label>
              <select value={form.contract_id} onChange={(e) => setForm({ ...form, contract_id: e.target.value, valor_renovado: 0 })}
                      disabled={isEdit}
                      className="tce-input w-full px-3 py-2 rounded-md text-sm" data-testid="ren-field-contract">
                <option value="">Selecione um contrato…</option>
                {contratos.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.numero_contrato || c.numero} — {(c.objeto || "").slice(0, 70)}{c.ciclo_atual > 1 ? ` (ciclo ${c.ciclo_atual})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          {infoContrato && (
            <div className="bg-[#0F172A] border border-[#334155] rounded-md p-4">
              <div className="uppercase-label mb-2">Contrato selecionado</div>
              <div className="font-medium">{infoContrato.numero}</div>
              <div className="text-xs text-[#94A3B8] mt-0.5">{infoContrato.objeto}</div>
              <div className="text-xs text-[#94A3B8] mt-0.5">Fornecedor: {infoContrato.fornecedor}</div>
              <div className="grid grid-cols-3 gap-3 mt-3 text-sm">
                <div className="bg-[#1E293B] p-2 rounded">
                  <div className="uppercase-label text-[10px]">Valor atual</div>
                  <div className="font-mono text-[#E9EEF5] mt-0.5">{fmtMoeda(infoContrato.valor_original)}</div>
                </div>
                <div className="bg-[#1E293B] p-2 rounded">
                  <div className="uppercase-label text-[10px]">Executado (NFs)</div>
                  <div className="font-mono text-[#F87171] mt-0.5">{fmtMoeda(infoContrato.executado)}</div>
                </div>
                <div className="bg-[#1E293B] p-2 rounded">
                  <div className="uppercase-label text-[10px]">Saldo atual</div>
                  <div className="font-mono text-[#12B850] mt-0.5">{fmtMoeda(infoContrato.saldo)}</div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="uppercase-label block mb-1">Novo exercício *</label>
              <input type="number" value={form.novo_exercicio} onChange={(e) => setForm({ ...form, novo_exercicio: e.target.value })}
                      disabled={!editable}
                      className="tce-input w-full px-3 py-2 rounded-md text-sm" data-testid="ren-field-exercicio" />
            </div>
            <div>
              <label className="uppercase-label block mb-1">Valor renovado (R$) *</label>
              <input type="number" step="0.01" value={form.valor_renovado} onChange={(e) => setForm({ ...form, valor_renovado: e.target.value })}
                      disabled={!editable}
                      className="tce-input w-full px-3 py-2 rounded-md text-sm" data-testid="ren-field-valor" />
              <div className="text-[10px] text-[#64748B] mt-1">Valor global do contrato após a renovação</div>
            </div>
            <div className="md:col-span-2">
              <label className="uppercase-label block mb-1">Justificativa *</label>
              <textarea value={form.justificativa} onChange={(e) => setForm({ ...form, justificativa: e.target.value })}
                        disabled={!editable} rows={3}
                        placeholder="Fundamentação para a renovação do saldo (novo exercício, recomposição, etc.)"
                        className="tce-input w-full px-3 py-2 rounded-md text-sm resize-none"
                        data-testid="ren-field-justificativa" />
            </div>
          </div>

          {current?.status === "aprovada" && (
            <div className="bg-[#002776]/15 border border-[#002776]/40 rounded-md p-4 flex items-start gap-3">
              <Warning size={18} className="text-[#FFDF00] mt-0.5" />
              <div className="text-xs text-[#E9EEF5]">
                <strong>Pronta para executar.</strong> Ao executar: as NFs lançadas serão <b>congeladas no ciclo {current.ciclo_fechado}</b> (histórico preservado),
                o <b>saldo utilizado será zerado</b>, o valor global passará a <b>{fmtMoeda(current.valor_renovado)}</b>
                {" "}e o contrato avança para o <b>ciclo {current.ciclo_novo}</b>. <u>Esta operação é irreversível.</u>
              </div>
            </div>
          )}

          {current?.status === "executada" && (
            <div className="bg-[#009C3B]/10 border border-[#009C3B]/40 rounded-md p-4">
              <div className="flex items-start gap-3">
                <CheckCircle size={18} className="text-[#12B850] mt-0.5" />
                <div className="text-xs">
                  <strong className="text-[#12B850]">Ciclo fechado com sucesso.</strong> Executado por <code>{current.executed_by}</code> em {new Date(current.executed_at).toLocaleString("pt-BR")}.
                  <br />Ciclo <b>{current.ciclo_fechado}</b> arquivado com valor executado de <b>{fmtMoeda(current.valor_executado_no_fechamento)}</b>.
                  Contrato agora opera no <b>ciclo {current.ciclo_novo}</b> com saldo renovado de <b>{fmtMoeda(current.valor_renovado)}</b>.
                </div>
              </div>
            </div>
          )}

          {current && (
            <div className="border-t border-[#334155] pt-4">
              <div className="uppercase-label mb-2">Histórico de tramitação</div>
              <div className="space-y-1.5">
                {(current.historico || []).map((h, i) => {
                  const smH = STATUS_META[h.status] || STATUS_META.rascunho;
                  return (
                    <div key={i} className="flex items-start gap-2 text-xs bg-[#0F172A] border border-[#334155] rounded px-3 py-2">
                      <Clock size={13} className="text-[#94A3B8] mt-0.5" />
                      <div className="flex-1">
                        <span className={`inline-block px-2 py-0.5 rounded border ${smH.bg} ${smH.text} ${smH.border} mr-2`}>{smH.label}</span>
                        <span className="text-[#94A3B8]">{h.obs}</span>
                        {h.parecer && <div className="text-[#E9EEF5] mt-1 italic">"{h.parecer}"</div>}
                        <div className="text-[#64748B] mt-0.5">por {h.por} · {new Date(h.em).toLocaleString("pt-BR")}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {current && current.status !== "executada" && (
            <div className="border-t border-[#334155] pt-4">
              <label className="uppercase-label block mb-1">Parecer / Observação</label>
              <textarea value={parecer} onChange={(e) => setParecer(e.target.value)} rows={2}
                        placeholder="Parecer para aprovação, rejeição ou execução…"
                        className="tce-input w-full px-3 py-2 rounded-md text-sm resize-none"
                        data-testid="ren-field-parecer" />
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[#334155] flex items-center justify-between gap-2">
          <div className="flex gap-2">
            {current && current.status !== "executada" && (
              <button onClick={excluir} className="px-3 py-2 rounded-md border border-red-500/40 text-red-400 text-xs flex items-center gap-1"
                      data-testid="ren-excluir">
                <Trash size={13} /> Excluir
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#334155] text-sm" data-testid="ren-cancel">
              Fechar
            </button>
            {editable && (
              <button onClick={salvar} disabled={busy}
                      className="px-4 py-2 rounded-md bg-[#002776] text-white font-medium text-sm flex items-center gap-2 disabled:opacity-50"
                      data-testid="ren-save">
                <PencilSimple size={13} weight="bold" /> {busy ? "Salvando…" : isEdit ? "Salvar" : "Criar renovação"}
              </button>
            )}
            {current?.status === "rascunho" && (
              <button onClick={() => transicao("submeter", "submeter")} disabled={busy}
                      className="px-4 py-2 rounded-md bg-[#FFDF00] text-[#002776] font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
                      data-testid="ren-submeter">
                <PaperPlaneRight size={13} weight="bold" /> Submeter
              </button>
            )}
            {current?.status === "em_analise" && canApprove && (
              <>
                <button onClick={() => transicao("rejeitar", "rejeitar")} disabled={busy}
                        className="px-4 py-2 rounded-md border border-red-500 text-red-400 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                        data-testid="ren-rejeitar">
                  <XCircle size={13} weight="bold" /> Rejeitar
                </button>
                <button onClick={() => transicao("aprovar", "aprovar")} disabled={busy}
                        className="px-4 py-2 rounded-md bg-[#1E4FBF] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                        data-testid="ren-aprovar">
                  <CheckCircle size={13} weight="bold" /> Aprovar
                </button>
              </>
            )}
            {current?.status === "aprovada" && canApprove && (
              <button onClick={() => transicao("executar", "executar",
                "ATENÇÃO: Esta operação é IRREVERSÍVEL. Confirma o fechamento do ciclo e a renovação do saldo?")}
                      disabled={busy}
                      className="px-4 py-2 rounded-md bg-[#009C3B] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                      data-testid="ren-executar">
                <Lightning size={13} weight="fill" /> Executar renovação
              </button>
            )}
            {current?.status === "rejeitada" && (
              <button onClick={() => transicao("reabrir", "submeter")} disabled={busy}
                      className="px-4 py-2 rounded-md border border-[#FFDF00]/40 text-[#FFDF00] text-sm font-medium">
                Reabrir análise
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
